import { Link } from "react-router-dom";
import { ScoreGauge } from "@/components/ScoreGauge";
import { Copy, Download, AlertTriangle, AlertCircle, Info, CheckCircle2, ExternalLink, Lock, Sparkles } from "lucide-react";
import { useState } from "react";
import { trackEvent } from "@/lib/analytics";

const LEVEL_META = {
  error: { color: "#E03E3E", Icon: AlertCircle, label: "Critical" },
  warning: { color: "#D9730D", Icon: AlertTriangle, label: "Warning" },
  info: { color: "#2383E2", Icon: Info, label: "Info" },
};

const PRIO_STYLE = {
  P0: { bg: "callout-red", text: "text-[#B0261D]", label: "P0 · High" },
  P1: { bg: "callout-orange", text: "text-[#B25E00]", label: "P1 · Medium" },
  P2: { bg: "callout-blue", text: "text-[#2383E2]", label: "P2 · Low" },
};

/**
 * Shared results renderer used by both authenticated ScanResult page
 * and public/guest preview page.
 * Props: scan (result object), showGuestBanner (bool), backHref
 */
export default function ScanResultView({ scan, showGuestBanner = false, backHref = "/dashboard" }) {
  const [copied, setCopied] = useState(false);
  const ai = scan.ai || {};
  const signals = scan.signals || {};

  const copyReport = async () => {
    await navigator.clipboard.writeText(buildReportText(scan));
    setCopied(true);
    trackEvent("report_copied", { authed: !showGuestBanner });
    setTimeout(() => setCopied(false), 2000);
  };

  const downloadReport = () => {
    const text = buildReportText(scan);
    const blob = new Blob([text], { type: "text/plain" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `scan-${(scan.id || "guest")}.txt`;
    a.click();
    URL.revokeObjectURL(url);
    trackEvent("report_downloaded", { authed: !showGuestBanner });
  };

  return (
    <div className="bg-white text-[#37352F] min-h-[calc(100vh-3.5rem)]">
      <div className="max-w-5xl mx-auto px-6 py-10">
        {!showGuestBanner && (
          <Link to={backHref} data-testid="back-to-dashboard-link" className="inline-flex items-center gap-1.5 text-sm text-[#787774] hover:text-[#37352F] mb-6">
            ← Back to dashboard
          </Link>
        )}

        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4 mb-8">
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-3 mb-2">
              <span className="notion-emoji-box callout-blue">📄</span>
              <h1 className="font-serif-h text-3xl md:text-4xl font-semibold tracking-tight truncate" data-testid="scan-title">
                {scan.url}
              </h1>
            </div>
            <div className="ml-[52px] flex flex-wrap gap-x-5 gap-y-1 text-sm text-[#787774]">
              <span>{new Date(scan.created_at).toLocaleString()}</span>
              {scan.target_keyword && <span>Target: <span className="text-[#37352F] font-medium">{scan.target_keyword}</span></span>}
              {scan.url && scan.url.startsWith("http") && (
                <a href={scan.url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-[#2383E2] hover:underline"><ExternalLink className="w-3 h-3" /> Open</a>
              )}
            </div>
          </div>
          {!showGuestBanner && (
            <div className="flex gap-2 shrink-0">
              <button data-testid="copy-report-button" onClick={copyReport} className="border border-[#EBEBEA] px-3 py-2 rounded-md text-sm text-[#37352F] hover:bg-[#F7F7F5] flex items-center gap-2">
                <Copy className="w-3.5 h-3.5" /> {copied ? "Copied" : "Copy"}
              </button>
              <button data-testid="download-report-button" onClick={downloadReport} className="bg-[#2383E2] text-white px-3 py-2 rounded-md text-sm font-medium hover:bg-[#1A6FCC] flex items-center gap-2">
                <Download className="w-3.5 h-3.5" /> Download
              </button>
            </div>
          )}
        </div>

        {/* Guest Banner (top) */}
        {showGuestBanner && (
          <div className="callout-yellow rounded-lg p-5 mb-8 flex flex-col md:flex-row md:items-center gap-4 justify-between" data-testid="guest-signup-banner-top">
            <div className="flex gap-3 items-start">
              <span className="text-2xl">🎁</span>
              <div>
                <div className="font-semibold text-[#37352F]">Free scan — no signup required.</div>
                <p className="text-sm text-[#37352F]">Create a free account to save this report, get unlimited scans, and track improvements over time.</p>
              </div>
            </div>
            <Link to="/signup" data-testid="banner-signup-cta" className="bg-[#2383E2] text-white font-medium px-5 py-2.5 rounded-md text-sm hover:bg-[#1A6FCC] transition-colors whitespace-nowrap flex items-center gap-2 justify-center">
              <Sparkles className="w-4 h-4" /> Save this scan — free
            </Link>
          </div>
        )}

        {/* AI Summary */}
        {ai.summary && (
          <div className="callout-yellow rounded-lg p-5 mb-8 flex gap-4 animate-fade-up" data-testid="ai-summary-card">
            <span className="text-2xl">✨</span>
            <div>
              <div className="font-semibold text-[#37352F] mb-1">Executive summary</div>
              <p className="text-[#37352F] leading-relaxed">{ai.summary}</p>
            </div>
          </div>
        )}

        {/* Scores */}
        <div className="grid md:grid-cols-2 gap-4 mb-8">
          <ScoreGauge label="SEO Score" value={scan.seo_score} emoji="🔍" testId="seo-score-card" />
          <ScoreGauge label="GEO Score" value={scan.geo_score} emoji="🤖" testId="geo-score-card" />
        </div>

        {/* Signals */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-12">
          {[
            { emoji: "📝", label: "Words", value: signals.word_count || 0, bg: "callout-blue" },
            { emoji: "🏷️", label: "H1 / H2", value: `${(signals.headings_count || {}).h1 || 0} / ${(signals.headings_count || {}).h2 || 0}`, bg: "callout-purple" },
            { emoji: "🔖", label: "Schema types", value: (signals.jsonld_types || []).length, bg: "callout-green" },
            { emoji: "🖼️", label: "Images (no alt)", value: `${signals.images_missing_alt || 0}/${signals.images_total || 0}`, bg: "callout-orange" },
          ].map((s) => (
            <div key={s.label} className="border border-[#EBEBEA] rounded-lg p-4">
              <div className="flex items-center gap-2 mb-2">
                <span className={`notion-emoji-box ${s.bg}`} style={{ width: 28, height: 28, fontSize: 16 }}>{s.emoji}</span>
                <span className="text-xs text-[#787774]">{s.label}</span>
              </div>
              <div className="text-2xl font-semibold text-[#37352F]">{s.value}</div>
            </div>
          ))}
        </div>

        {/* Action Plan — for guests, show first 2 and blur the rest */}
        <section className="mb-14" data-testid="action-plan-section">
          <div className="flex items-center gap-3 mb-5">
            <span className="notion-emoji-box callout-green">✅</span>
            <h2 className="font-serif-h text-2xl md:text-3xl font-semibold tracking-tight">Prioritized action plan</h2>
            <span className="text-sm text-[#9B9A97]">({(ai.action_plan || []).length} tasks)</span>
          </div>
          {(ai.action_plan || []).length === 0 ? (
            <div className="border border-[#EBEBEA] rounded-lg p-6 text-[#9B9A97] text-sm">No AI plan available for this scan.</div>
          ) : (
            <div className="space-y-3">
              {(ai.action_plan || []).map((step, i) => {
                const ps = PRIO_STYLE[step.priority] || PRIO_STYLE.P2;
                const isLocked = showGuestBanner && i >= 2;
                return (
                  <div key={i} className={`border border-[#EBEBEA] rounded-lg p-5 relative animate-fade-up ${isLocked ? "select-none" : "hover:bg-[#FAFAF9] transition-colors"}`} data-testid={`action-item-${i}`}>
                    <div className={isLocked ? "blur-sm pointer-events-none" : ""}>
                      <div className="flex flex-wrap items-center gap-2 mb-3">
                        <span className={`${ps.bg} ${ps.text} rounded-md px-2 py-0.5 text-xs font-semibold`}>{ps.label}</span>
                        <span className="callout-gray text-[#787774] rounded-md px-2 py-0.5 text-xs">{step.category}</span>
                        <span className="ml-auto text-xs text-[#9B9A97]">Impact: {step.impact}</span>
                      </div>
                      <h3 className="font-semibold text-lg mb-2 text-[#37352F]">{step.title}</h3>
                      <ol className="space-y-1.5 list-decimal list-inside text-sm text-[#37352F]">
                        {(step.steps || []).map((s, j) => <li key={j}>{s}</li>)}
                      </ol>
                    </div>
                    {isLocked && (
                      <div className="absolute inset-0 flex items-center justify-center bg-white/70">
                        <Link to="/signup" data-testid="locked-signup-cta" className="bg-[#2383E2] text-white font-medium px-4 py-2 rounded-md text-sm flex items-center gap-2 hover:bg-[#1A6FCC]">
                          <Lock className="w-3.5 h-3.5" /> Sign up free to unlock
                        </Link>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </section>

        {/* Issues */}
        <section className="mb-14 grid md:grid-cols-2 gap-4">
          <IssueList title="SEO issues" emoji="🔍" bg="callout-blue" issues={scan.seo_issues || []} testId="seo-issues" />
          <IssueList title="GEO issues" emoji="🤖" bg="callout-purple" issues={scan.geo_issues || []} testId="geo-issues" />
        </section>

        {/* Keywords (guest sees teaser) */}
        {(ai.keywords || []).length > 0 && (
          <section className="mb-14" data-testid="keywords-section">
            <div className="flex items-center gap-3 mb-5">
              <span className="notion-emoji-box callout-orange">🎯</span>
              <h2 className="font-serif-h text-2xl md:text-3xl font-semibold tracking-tight">Suggested keywords</h2>
            </div>
            <div className="grid md:grid-cols-2 gap-3 relative">
              {ai.keywords.slice(0, showGuestBanner ? 3 : ai.keywords.length).map((k, i) => (
                <div key={i} className="border border-[#EBEBEA] rounded-lg p-4 hover:bg-[#FAFAF9] transition-colors">
                  <div className="font-medium text-[#37352F] mb-2">{k.keyword}</div>
                  <div className="flex gap-3 text-xs text-[#787774]">
                    <span className="callout-gray rounded-md px-2 py-0.5">Intent: <span className="text-[#37352F]">{k.intent}</span></span>
                    <span className="callout-gray rounded-md px-2 py-0.5">Difficulty: <span className="text-[#37352F]">{k.difficulty}</span></span>
                  </div>
                </div>
              ))}
              {showGuestBanner && ai.keywords.length > 3 && (
                <div className="border border-dashed border-[#EBEBEA] rounded-lg p-4 flex items-center justify-center">
                  <Link to="/signup" className="text-[#2383E2] font-medium text-sm hover:underline">+ {ai.keywords.length - 3} more — sign up to view</Link>
                </div>
              )}
            </div>
          </section>
        )}

        {/* GEO Tactics */}
        {(ai.geo_tactics || []).length > 0 && (
          <section className="mb-14" data-testid="geo-tactics-section">
            <div className="flex items-center gap-3 mb-5">
              <span className="notion-emoji-box callout-purple">🚀</span>
              <h2 className="font-serif-h text-2xl md:text-3xl font-semibold tracking-tight">Rank in AI engines</h2>
            </div>
            <div className="callout-purple rounded-lg p-5 space-y-3">
              {(ai.geo_tactics || []).slice(0, showGuestBanner ? 2 : ai.geo_tactics.length).map((t, i) => (
                <div key={i} className="flex gap-3">
                  <CheckCircle2 className="w-4 h-4 text-[#5B3E82] shrink-0 mt-1" />
                  <span className="text-[#37352F]">{t}</span>
                </div>
              ))}
              {showGuestBanner && ai.geo_tactics.length > 2 && (
                <div className="pt-2 mt-2 border-t border-[#D6C8E5]">
                  <Link to="/signup" className="text-[#5B3E82] font-medium text-sm hover:underline">+ {ai.geo_tactics.length - 2} more tactics — sign up to view →</Link>
                </div>
              )}
            </div>
          </section>
        )}

        {/* Bottom guest banner */}
        {showGuestBanner && (
          <div className="callout-blue rounded-lg p-8 text-center" data-testid="guest-signup-banner-bottom">
            <span className="text-3xl block mb-3">🚀</span>
            <h3 className="font-serif-h text-2xl font-semibold mb-2 text-[#37352F]">Save this scan &amp; unlock everything</h3>
            <p className="text-[#37352F] mb-5 max-w-lg mx-auto">Get unlimited scans, the full action plan, all keyword ideas, GEO tactics, and scan history to track your progress.</p>
            <Link to="/signup" data-testid="bottom-signup-cta" className="inline-flex items-center gap-2 bg-[#2383E2] text-white font-medium px-6 py-3 rounded-md hover:bg-[#1A6FCC] transition-colors">
              Create free account →
            </Link>
          </div>
        )}
      </div>
    </div>
  );
}

function IssueList({ title, emoji, bg, issues, testId }) {
  return (
    <div className="border border-[#EBEBEA] rounded-lg p-5" data-testid={testId}>
      <div className="flex items-center gap-2 mb-4">
        <span className={`notion-emoji-box ${bg}`} style={{ width: 32, height: 32, fontSize: 18 }}>{emoji}</span>
        <h3 className="font-semibold text-lg text-[#37352F]">{title}</h3>
        <span className="ml-auto text-sm text-[#9B9A97]">{issues.length}</span>
      </div>
      {issues.length === 0 ? (
        <div className="flex items-center gap-2 text-sm text-[#0F7B0F]">
          <CheckCircle2 className="w-4 h-4" /> No issues found.
        </div>
      ) : (
        <ul className="space-y-3">
          {issues.map((iss, i) => {
            const m = LEVEL_META[iss.level] || LEVEL_META.info;
            return (
              <li key={i} className="flex gap-2.5">
                <m.Icon className="w-4 h-4 shrink-0 mt-0.5" style={{ color: m.color }} />
                <div className="min-w-0">
                  <div className="text-xs font-medium" style={{ color: m.color }}>{m.label} · {iss.area}</div>
                  <div className="text-sm text-[#37352F] mt-0.5">{iss.message}</div>
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

function buildReportText(scan) {
  const lines = [];
  lines.push(`RANK NOTES — SEO + GEO SCAN REPORT`);
  lines.push(`=`.repeat(60));
  lines.push(`URL: ${scan.url}`);
  lines.push(`Date: ${new Date(scan.created_at).toLocaleString()}`);
  if (scan.target_keyword) lines.push(`Target keyword: ${scan.target_keyword}`);
  lines.push(``);
  lines.push(`SEO SCORE: ${scan.seo_score}/100`);
  lines.push(`GEO SCORE: ${scan.geo_score}/100`);
  lines.push(``);
  lines.push(`SUMMARY`);
  lines.push(`-`.repeat(60));
  lines.push(scan.ai?.summary || "");
  lines.push(``);
  lines.push(`ACTION PLAN`);
  lines.push(`-`.repeat(60));
  (scan.ai?.action_plan || []).forEach((s, i) => {
    lines.push(`${i + 1}. [${s.priority}] ${s.title} — ${s.category}`);
    (s.steps || []).forEach((st) => lines.push(`   • ${st}`));
    lines.push(``);
  });
  lines.push(`SEO ISSUES`);
  lines.push(`-`.repeat(60));
  (scan.seo_issues || []).forEach((i) => lines.push(`[${i.level.toUpperCase()}] ${i.area}: ${i.message}`));
  lines.push(``);
  lines.push(`GEO ISSUES`);
  lines.push(`-`.repeat(60));
  (scan.geo_issues || []).forEach((i) => lines.push(`[${i.level.toUpperCase()}] ${i.area}: ${i.message}`));
  lines.push(``);
  lines.push(`KEYWORDS`);
  lines.push(`-`.repeat(60));
  (scan.ai?.keywords || []).forEach((k) => lines.push(`• ${k.keyword} (intent: ${k.intent}, difficulty: ${k.difficulty})`));
  return lines.join("\n");
}
